import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  LinkOff as UnlinkIcon,
  Link as LinkIcon,
  CancelOutlined as CancelIcon,
} from '@mui/icons-material';
import {
  getPreOrderDetail,
  linkPreOrderPickups,
  listIncomingPickups,
  unlinkPreOrderPickup,
  updatePreOrderStatus,
} from '../../services/pre-order.service';
import { PreOrderStatus, type IncomingPickupOrder, type PreOrderDetail } from '../../types/pre-order.type';
import { formatLocalizedDate } from '../../helpers/date-locale.helper';
import { getApiErrorDetail } from '../../helpers/api-error.helper';

export const PRE_ORDER_DETAIL_QUERY_KEY = 'pre-order-detail';

const OPEN_STATUSES = new Set<string>([PreOrderStatus.PENDING, PreOrderStatus.PARTIALLY_FULFILLED]);

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

function statusColor(status: string): 'default' | 'warning' | 'info' | 'success' | 'error' {
  if (status === PreOrderStatus.PENDING) return 'warning';
  if (status === PreOrderStatus.PARTIALLY_FULFILLED) return 'info';
  if (status === PreOrderStatus.COMPLETED) return 'success';
  if (status === PreOrderStatus.CANCELLED) return 'error';
  return 'default';
}

function formatFallbackLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function preOrderMaterialLabel(order: { material_name_snapshot?: string | null; material_category: string }, t: TFunction): string {
  const name = order.material_name_snapshot?.trim();
  if (name) return name;
  return t(`categories.${order.material_category}`, { defaultValue: formatFallbackLabel(order.material_category) });
}

interface Props {
  open: boolean;
  preOrderId: string | null;
  organizationId: string;
  canWrite: boolean;
  onClose: () => void;
  /** Called after any change so the list behind the drawer can refresh. */
  onChanged: () => void;
}

const PreOrderDetailDrawer: React.FC<Props> = ({ open, preOrderId, organizationId, canWrite, onClose, onChanged }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedIncoming, setSelectedIncoming] = useState<Set<string>>(() => new Set());

  const detailQuery = useQuery({
    queryKey: [PRE_ORDER_DETAIL_QUERY_KEY, preOrderId],
    queryFn: () => getPreOrderDetail(preOrderId!),
    enabled: open && Boolean(preOrderId),
  });
  const order = detailQuery.data;
  const isOpenOrder = Boolean(order && OPEN_STATUSES.has(order.status));
  const canLink = canWrite && isOpenOrder;

  const incomingQuery = useQuery({
    queryKey: ['incoming-pickups', organizationId, order?.material_category],
    queryFn: () => listIncomingPickups(organizationId, { material_category: order?.material_category }),
    enabled: open && canLink && Boolean(order),
  });

  // The backend refuses a different waste type in the same category, so only
  // offer pickups that would actually link.
  const linkableIncoming = useMemo<IncomingPickupOrder[]>(() => {
    const rows = incomingQuery.data?.pickup_orders ?? [];
    if (!order?.material_id) return rows;
    return rows.filter((row) => {
      const pickupMaterial = row.material_id ?? row.material_type?.material_id ?? null;
      return !pickupMaterial || pickupMaterial === order.material_id;
    });
  }, [incomingQuery.data, order?.material_id]);

  const num = (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  const dateOf = (iso: string) => formatLocalizedDate(new Date(iso), i18n.language);

  const afterChange = async (next?: PreOrderDetail) => {
    if (next && preOrderId) queryClient.setQueryData([PRE_ORDER_DETAIL_QUERY_KEY, preOrderId], next);
    await queryClient.invalidateQueries({ queryKey: [PRE_ORDER_DETAIL_QUERY_KEY, preOrderId] });
    await queryClient.invalidateQueries({ queryKey: ['incoming-pickups', organizationId] });
    await queryClient.invalidateQueries({ queryKey: ['recycler-pre-orders-overview', organizationId] });
    onChanged();
  };

  const run = async (action: () => Promise<PreOrderDetail | void>) => {
    setBusy(true);
    setActionError(null);
    try {
      const next = await action();
      await afterChange(next ?? undefined);
    } catch (error) {
      setActionError(getApiErrorDetail(error) ?? t('orders.actionError'));
    } finally {
      setBusy(false);
    }
  };

  const cancelOrder = () => {
    if (!order || !window.confirm(t('orders.cancelConfirm'))) return;
    void run(async () => {
      await updatePreOrderStatus(order.id, { status: PreOrderStatus.CANCELLED });
    });
  };

  const unlink = (pickupOrderId: string) => {
    if (!order || !window.confirm(t('orders.unlinkConfirm'))) return;
    void run(() => unlinkPreOrderPickup(order.id, pickupOrderId));
  };

  const linkSelected = () => {
    if (!order || selectedIncoming.size === 0) return;
    const ids = Array.from(selectedIncoming);
    void run(async () => {
      const next = await linkPreOrderPickups(order.id, ids);
      setSelectedIncoming(new Set());
      return next;
    });
  };

  const toggleIncoming = (id: string) =>
    setSelectedIncoming((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleClose = () => {
    setActionError(null);
    setSelectedIncoming(new Set());
    onClose();
  };

  const percent = order ? Math.min(100, Math.max(0, Math.round(order.fulfillment_percent))) : 0;

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('orders.detailsTitle')}
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label={t('orders.close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />

      {detailQuery.isLoading && (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {detailQuery.isError && (
        <Alert severity="error" sx={{ m: 2.5 }}>
          {t('orders.loadDetailError')}
        </Alert>
      )}

      {order && (
        <Box sx={{ p: 2.5, overflowY: 'auto' }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {preOrderMaterialLabel(order, t)}
            </Typography>
            <Chip size="small" variant="outlined" label={t(`categories.${order.material_category}`, { defaultValue: formatFallbackLabel(order.material_category) })} />
            <Chip size="small" color={statusColor(order.status)} label={t(`orderStatus.${order.status}`, { defaultValue: formatFallbackLabel(order.status) })} />
            {order.origin === 'ops' && (
              <Tooltip title={t('orders.originOpsHint')}>
                <Chip size="small" color="secondary" variant="outlined" label={`${t('orders.origin')}: ${t('orders.originOps')}`} />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            #{shortId(order.id)} · {t('orders.created')} {dateOf(order.created_at)}
            {order.data?.reference ? ` · ${t('orders.reference')}: ${String(order.data.reference)}` : ''}
          </Typography>

          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              [t('orders.orderedKg'), num(order.amount_kg)],
              [t('orders.fulfilledKg'), num(order.fulfilled_kg)],
              [t('orders.remainingKg'), num(order.remaining_kg)],
            ].map(([label, value]) => (
              <Grid item xs={4} key={label}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px' }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={percent}
              color={order.status === PreOrderStatus.COMPLETED ? 'success' : 'primary'}
              sx={{ flex: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 44, textAlign: 'right' }}>
              {Math.round(order.fulfillment_percent)}%
            </Typography>
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('orders.classification')}
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {[
              [t('orders.amount'), `${num(order.amount)} ${order.amount_unit ? t(`units.${order.amount_unit}`, { defaultValue: order.amount_unit }) : ''}`],
              [t('orders.ewcCode'), order.regulatory_code_snapshot ? `${order.regulatory_code_snapshot}${order.regulatory_code_description_snapshot ? ` · ${order.regulatory_code_description_snapshot}` : ''}` : t('orders.notSet')],
              [t('orders.mwanSource'), order.mwan_source_category_snapshot ?? t('orders.notSet')],
              [t('orders.physicalState'), order.physical_state_snapshot ? formatFallbackLabel(order.physical_state_snapshot) : t('orders.notSet')],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} key={label}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
                <Typography variant="body2">{value}</Typography>
              </Grid>
            ))}
            {order.data?.notes && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t('orders.notes')}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{String(order.data.notes)}</Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('orders.linkedPickups')} ({order.linked_pickups.length})
          </Typography>
          {order.linked_pickups.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('orders.noLinkedPickups')}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: '8px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('orders.pickup')}</TableCell>
                    <TableCell>{t('orders.wasteOutlet')}</TableCell>
                    <TableCell>{t('orders.pickupStatus')}</TableCell>
                    <TableCell>{t('orders.deliveryPhase')}</TableCell>
                    <TableCell align="right">{t('orders.verifiedWeight')}</TableCell>
                    {canLink && <TableCell align="right" />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.linked_pickups.map((pickup) => (
                    <TableRow key={pickup.pickup_order_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>#{shortId(pickup.pickup_order_id)}</Typography>
                        <Typography variant="caption" color="text.secondary">{dateOf(pickup.created_at)}</Typography>
                      </TableCell>
                      <TableCell>{pickup.waste_outlet_name ?? '—'}</TableCell>
                      <TableCell>{t(`pickupStatus.${pickup.status}`, { defaultValue: formatFallbackLabel(pickup.status) })}</TableCell>
                      <TableCell>{pickup.delivery_phase ? t(`deliveryPhase.${pickup.delivery_phase}`, { defaultValue: formatFallbackLabel(pickup.delivery_phase) }) : '—'}</TableCell>
                      <TableCell align="right">
                        {pickup.verified_weight_kg != null
                          ? `${num(pickup.verified_weight_kg)} ${t('units.kg')}`
                          : `${num(pickup.amount)} ${t(`units.${pickup.amount_unit}`, { defaultValue: pickup.amount_unit })}`}
                      </TableCell>
                      {canLink && (
                        <TableCell align="right">
                          <Tooltip title={t('orders.unlink')}>
                            <span>
                              <IconButton size="small" disabled={busy} onClick={() => unlink(pickup.pickup_order_id)}>
                                <UnlinkIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {canLink && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <LinkIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('orders.linkPickups')}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('orders.linkHint')}
              </Typography>
              {incomingQuery.isLoading ? (
                <CircularProgress size={20} />
              ) : linkableIncoming.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('orders.noIncomingPickups')}</Typography>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', mb: 1 }}>
                    <Table size="small">
                      <TableBody>
                        {linkableIncoming.map((pickup) => (
                          <TableRow key={pickup.id} hover onClick={() => toggleIncoming(pickup.id)} sx={{ cursor: 'pointer' }}>
                            <TableCell padding="checkbox">
                              <Checkbox size="small" checked={selectedIncoming.has(pickup.id)} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {pickup.material_name_snapshot?.trim() || pickup.material_type?.name || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                #{shortId(pickup.id)} · {dateOf(pickup.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>{t(`pickupStatus.${pickup.status}`, { defaultValue: formatFallbackLabel(pickup.status) })}</TableCell>
                            <TableCell align="right">
                              {num(pickup.amount)} {pickup.amount_unit ? t(`units.${pickup.amount_unit}`, { defaultValue: pickup.amount_unit }) : ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<LinkIcon />}
                    disabled={busy || selectedIncoming.size === 0}
                    onClick={linkSelected}
                  >
                    {t('orders.linkSelected')} ({selectedIncoming.size})
                  </Button>
                </>
              )}
            </Box>
          )}

          {canWrite && isOpenOrder && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Button color="error" variant="outlined" startIcon={<CancelIcon />} disabled={busy} onClick={cancelOrder}>
                {t('orders.cancelOrder')}
              </Button>
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default PreOrderDetailDrawer;
